import React, { useState, useMemo, useEffect } from "react";
import "../../style/pages/categoriespage/productreview.css";

const ProductReview = () => {
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [likes, setLikes] = useState({}); // { reviewId: currentLikeCount }

  const REVIEWS_PER_PAGE = 5;

  // === Base Reviews (12 real + 38 generated = 50 total) ===
  const baseReviews = [
    {
      id: 1,
      rating: 5,
      title: "Absolutely love this knife skin! The detail is insane.",
      date: "July 2, 2020 03:23 PM",
      author: "Darrell Steward",
      topics: ["Skin Design Quality", "Match with Description"],
      likes: 126,
    },
    {
      id: 2,
      rating: 5,
      title: "Got my gloves at a great price — looks even better in-game!",
      date: "July 2, 2020 1:04 PM",
      author: "Darlene Robertson",
      topics: ["Product Price", "Seller Services"],
      likes: 82,
    },
    {
      id: 3,
      rating: 4,
      title: "Fast delivery and authentic skin. Would love to see more rare cases.",
      date: "June 26, 2020 10:03 PM",
      author: "Kathryn Murphy",
      topics: ["Shipment", "Skin Design Quality"],
      likes: 67,
    },
    {
      id: 4,
      rating: 5,
      title: "Best marketplace for CS skins! Got my M4A1-S in seconds.",
      date: "July 7, 2020 10:14 AM",
      author: "Ronald Richards",
      topics: ["Seller Services", "Skin Design Quality"],
      likes: 124,
    },
    {
      id: 5,
      rating: 5,
      title: "Great UI, smooth checkout — and the skins are 100% legit!",
      date: "July 10, 2020 08:47 PM",
      author: "Courtney Henry",
      topics: ["Seller Services", "Match with Description"],
      likes: 93,
    },
    {
      id: 6,
      rating: 4,
      title: "Had a small delay, but customer support was super helpful.",
      date: "July 12, 2020 11:30 AM",
      author: "Wade Warren",
      topics: ["Seller Services", "Shipment"],
      likes: 58,
    },
    {
      id: 7,
      rating: 5,
      title: "The AK skin I bought looks fire Totally worth it.",
      date: "July 15, 2020 04:52 PM",
      author: "Cameron Williamson",
      topics: ["Skin Design Quality", "Product Price"],
      likes: 102,
    },
    {
      id: 8,
      rating: 3,
      title: "Good selection but pricing could be better.",
      date: "July 16, 2020 09:15 AM",
      author: "Jennifer Wise",
      topics: ["Product Price"],
      likes: 45,
    },
    {
      id: 9,
      rating: 5,
      title: "Lightning fast transaction! Highly recommend.",
      date: "July 18, 2020 02:30 PM",
      author: "Albert Flores",
      topics: ["Seller Services"],
      likes: 89,
    },
    {
      id: 10,
      rating: 4,
      title: "Great quality skins, but wish there were more payment options.",
      date: "July 20, 2020 07:45 PM",
      author: "Ralph Edwards",
      topics: ["Skin Design Quality"],
      likes: 76,
    },
    {
      id: 11,
      rating: 5,
      title: "Perfect condition and exactly as described!",
      date: "July 22, 2020 11:20 AM",
      author: "Beverly Mitchell",
      topics: ["Match with Description", "Skin Design Quality"],
      likes: 134,
    },
    {
      id: 12,
      rating: 2,
      title: "Delivery took longer than expected.",
      date: "July 25, 2020 03:10 PM",
      author: "Jesse Sims",
      topics: ["Shipment"],
      likes: 23,
    },
  ];

  // === Generate 50+ reviews ===
  const allReviews = useMemo(() => {
    const reviews = [...baseReviews];
    for (let i = 13; i <= 50; i++) {
      reviews.push({
        id: i,
        rating: Math.floor(Math.random() * 5) + 1,
        title: `Review ${i} - Great experience with this skin purchase!`,
        date: `July ${Math.floor(Math.random() * 30 + 1)}, 2020 ${String(
          Math.floor(Math.random() * 12)
        ).padStart(2, "0")}:${String(Math.floor(Math.random() * 60)).padStart(
          2,
          "0"
        )} ${["AM", "PM"][Math.floor(Math.random() * 2)]}`,
        author: `User ${i}`,
        topics: [
          Math.random() > 0.5 ? "Skin Design Quality" : "Seller Services",
          Math.random() > 0.7 ? "Product Price" : null,
        ].filter(Boolean),
        likes: Math.floor(Math.random() * 150),
      });
    }
    return reviews;
  }, []);

  // === Filter Reviews ===
  const filteredReviews = useMemo(() => {
    return allReviews.filter((review) => {
      const ratingMatch =
        selectedRatings.length === 0 || selectedRatings.includes(review.rating);
      const topicMatch =
        selectedTopics.length === 0 ||
        selectedTopics.some((topic) => review.topics.includes(topic));
      return ratingMatch && topicMatch;
    });
  }, [allReviews, selectedRatings, selectedTopics]);

  // === Pagination Logic ===
  const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);

  const paginatedReviews = useMemo(() => {
    const start = (currentPage - 1) * REVIEWS_PER_PAGE;
    return filteredReviews.slice(start, start + REVIEWS_PER_PAGE);
  }, [filteredReviews, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRatings, selectedTopics]);

  // === Like / Dislike Handlers ===
  const handleLike = (reviewId) => {
    setLikes((prev) => ({
      ...prev,
      [reviewId]:
        (prev[reviewId] ??
          allReviews.find((r) => r.id === reviewId)?.likes ??
          0) + 1,
    }));
  };

  const handleDislike = (reviewId) => {
    setLikes((prev) => {
      const current =
        prev[reviewId] ??
        allReviews.find((r) => r.id === reviewId)?.likes ??
        0;
      return { ...prev, [reviewId]: Math.max(0, current - 1) };
    });
  };

  // === Pagination Controls ===
  const goPrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const goNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  // === Filter Toggles ===
  const toggleRating = (rating) => {
    setSelectedRatings((prev) =>
      prev.includes(rating)
        ? prev.filter((r) => r !== rating)
        : [...prev, rating]
    );
  };

  const toggleTopic = (topic) => {
    setSelectedTopics((prev) =>
      prev.includes(topic)
        ? prev.filter((t) => t !== topic)
        : [...prev, topic]
    );
  };

  return (
    <div className="product-review-section">
      {/* ============== HEADER ============== */}
      <div className="review-header">
        <div className="review-summary">
          <div className="overall-rating">
            <span className="rating-number">4.5</span>
            <div className="rating-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <span key={star} className="star filled">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="16px"
                    viewBox="0 -960 960 960"
                    width="16px"
                    fill="#ff8c00"
                  >
                    <path d="m354-287 126-76 126 77-33-144 111-96-146-13-58-136-58 135-146 13 111 97-33 143ZM233-120l65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Zm247-350Z" />
                  </svg>
                </span>
              ))}
            </div>
            <span className="review-count">
              from {filteredReviews.length}{" "}
              {filteredReviews.length === 1 ? "review" : "reviews"}
            </span>
          </div>

          <div className="rating-breakdown">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = allReviews.filter((r) => r.rating === rating).length;
              const percentage =
                allReviews.length > 0
                  ? (count / allReviews.length) * 100
                  : 0;
              return (
                <div key={rating} className="rating-bar-item">
                  <span className="rating-label">
                    {rating}.0{" "}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="14px"
                      viewBox="0 -960 960 960"
                      width="14px"
                      fill="#ff8c00"
                      style={{ verticalAlign: "middle", marginLeft: "2px" }}
                    >
                      <path d="m354-287 126-76 126 77-33-144 111-96-146-13-58-136-58 135-146 13 111 97-33 143ZM233-120l65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Zm247-350Z" />
                    </svg>
                  </span>
                  <div className="rating-bar">
                    <div
                      className="rating-fill"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="rating-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ============== CONTENT WRAPPER ============== */}
      <div className="review-content-wrapper">
        {/* ============== FILTERS ============== */}
        <div className="review-filters">
          <div className="filter-section">
            <div className="filter-header">
              <span>Reviews Filter</span>
            </div>

            {/* Rating Filter */}
            <div className="filter-group">
              <div className="filter-group-title">
                <span>Rating</span>
                <span className="expand-icon"></span>
              </div>
              <div className="filter-options">
                {[5, 4, 3, 2, 1].map((rating) => (
                  <label key={rating} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRatings.includes(rating)}
                      onChange={() => toggleRating(rating)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="rating-label">
                      {rating}.0{" "}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="14px"
                        viewBox="0 -960 960 960"
                        width="14px"
                        fill="#ff8c00"
                        style={{ verticalAlign: "middle", marginLeft: "2px" }}
                      >
                        <path d="m354-287 126-76 126 77-33-144 111-96-146-13-58-136-58 135-146 13 111 97-33 143ZM233-120l65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Zm247-350Z" />
                      </svg>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Topic Filter */}
            <div className="filter-group">
              <div className="filter-group-title">
                <span>Review Topics</span>
                <span className="expand-icon"></span>
              </div>
              <div className="filter-options">
                {[
                  "Skin Design Quality",
                  "Rarity & Collection Value",
                  "Product Price",
                  "Shipment",
                  "Match with Description",
                ].map((topic) => (
                  <label key={topic} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTopics.includes(topic)}
                      onChange={() => toggleTopic(topic)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="filter-label">{topic}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ============== REVIEW LIST ============== */}
        <div className="review-list-section">
          <div className="review-list-header">
            <span className="section-title">
              Review Lists ({paginatedReviews.length} of {filteredReviews.length})
            </span>
          </div>

          <div className="reviews-list">
            {paginatedReviews.length > 0 ? (
              paginatedReviews.map((review) => {
                const currentLikes = likes[review.id] ?? review.likes;
                return (
                  <div key={review.id} className="review-item">
                    <div className="review-item-header">
                      <div className="review-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            className={`star ${star <= review.rating ? "filled" : ""}`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="16px"
                              viewBox="0 -960 960 960"
                              width="16px"
                              fill={star <= review.rating ? "#ff8c00" : "rgba(255,255,255,0.12)"}
                            >
                              <path d="m354-287 126-76 126 77-33-144 111-96-146-13-58-136-58 135-146 13 111 97-33 143ZM233-120l65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Zm247-350Z" />
                            </svg>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="review-title">{review.title}</div>
                    <div className="review-date">{review.date}</div>
                    <div className="review-footer">
                      <div className="review-author">
                        <div className="author-avatar">
                          {review.author.charAt(0)}
                        </div>
                        <span className="author-name">{review.author}</span>
                      </div>
                      <div className="review-actions">
                        <button
                          className="action-btn like-btn"
                          onClick={() => handleLike(review.id)}
                        >
                          <span className="thumbs-up">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="20px"
                              viewBox="0 -960 960 960"
                              width="20px"
                              fill="#ff8c00"
                            >
                              <path d="M720-120H280v-520l280-280 50 50q7 7 11.5 19t4.5 23v14l-44 174h258q32 0 56 24t24 56v80q0 7-2 15t-4 15L794-168q-9 20-30 34t-44 14Zm-360-80h360l120-280v-80H480l54-220-174 174v406Zm0-406v406-406Zm-80-34v80H160v360h120v80H80v-520h200Z" />
                            </svg>
                          </span>
                          <span>{currentLikes}</span>
                        </button>
                        <button
                          className="action-btn dislike-btn"
                          onClick={() => handleDislike(review.id)}
                        >
                          <span className="thumbs-down">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24px"
                              viewBox="0 -960 960 960"
                              width="24px"
                              fill="#ff8c00"
                            >
                              <path d="M240-840h440v520L400-40l-50-50q-7-7-11.5-19t-4.5-23v-14l44-174H120q-32 0-56-24t-24-56v-80q0-7 2-15t4-15l120-282q9-20 30-34t44-14Zm360 80H240L120-480v80h360l-54 220 174-174v-406Zm0 406v-406 406Zm80 34v-80h120v-360H680v-80h200v520H680Z" />
                            </svg>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-reviews">
                <p>No reviews match your filters.</p>
              </div>
            )}
          </div>

          {/* ============== 3-BUTTON PAGINATION ============== */}
          {filteredReviews.length > REVIEWS_PER_PAGE && (
            <div className="pagination">
              <button
                className="page-btn"
                disabled={currentPage === 1}
                onClick={goPrev}
              >
                ‹
              </button>

              <button className="page-btn page-number">
                {currentPage}/{totalPages}
              </button>

              <button
                className="page-btn next-btn"
                disabled={currentPage === totalPages}
                onClick={goNext}
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductReview;